// src/pages/Employee/EmployeeTypeAssignment.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import {
  User,
  ArrowLeft,
  RefreshCw,
  Trash2,
  Plus,
  X,
  Info,
  Shield,
  ShieldOff,
} from 'react-feather';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';
import BreadCrumb from '../../Components/Common/BreadCrumb';

// Types
interface Employee {
  id: number;
  name: string;
  employee_code: string;
  employee_type: number | null;
  geo_fence_enabled: boolean;
  designation_name: string;
  unit_name: string;
  status: number;
}

interface OfficeLocation {
  id: number;
  name: string;        // ✅ Fixed: Changed from office_name to name
  address: string | null;
  latitude: number;
  longitude: number;
  radius: number;
  status: number;
}

interface EmployeeLocation {
  id: number;
  employee_id: number;
  location_type: number;
  location_type_label?: string;
  office_location_id: number;
  status: number;
  office_location: OfficeLocation;
  created_at: string;
  updated_at: string;
}

interface EmployeeLocationsResponse {
  employee: {
    id: number;
    name: string;
    employee_code: string;
    employee_type: number | null;
    employee_type_label: string;
    geo_fence_enabled: boolean;
    status: number;
    status_label: string;
    designation: { id: number; title: string } | null;
    unit: { id: number; name: string } | null;
  };
  locations: EmployeeLocation[];
  summary: {
    total_locations: number;
    active_locations: number;
    total_office_locations: number;
    total_site_locations: number;
  };
}

interface AssignLocationPayload {
  employee_id: number;
  location_type: number;
  office_location_ids: number[];
}

const EMPLOYEE_TYPES = [
  { value: 1, label: 'Office' },
  { value: 2, label: 'Site Engineer' },
  { value: 3, label: 'Executive' },
];

const EmployeeTypeAssignment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [employeeLocations, setEmployeeLocations] = useState<EmployeeLocation[]>([]);
  const [locationSummary, setLocationSummary] = useState<{
    total_locations: number;
    active_locations: number;
    total_office_locations: number;
    total_site_locations: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Location data
  const [officeLocations, setOfficeLocations] = useState<OfficeLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Form state
  const [formData, setFormData] = useState<AssignLocationPayload>({
    employee_id: parseInt(id || '0'),
    location_type: 1,
    office_location_ids: [],
  });
  const [formError, setFormError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Employee type update
  const [selectedEmployeeType, setSelectedEmployeeType] = useState<number | null>(null);
  const [updatingType, setUpdatingType] = useState(false);

  // Geofence toggle
  const [geoFenceEnabled, setGeoFenceEnabled] = useState<boolean>(true);
  const [updatingGeoFence, setUpdatingGeoFence] = useState(false);

  // Select all state
  const [selectAll, setSelectAll] = useState(false);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Remove all modal
  const [removeAllModal, setRemoveAllModal] = useState(false);

  // ✅ Fetch employee data and locations from a single API
  const fetchEmployeeData = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get(`/type-assign/employees/${id}/locations`);
      const data = response.data || response;
      const result = data;

      if (result) {
        // ✅ Update employee info
        if (result.employee) {
          setEmployee({
            id: result.employee.id,
            name: result.employee.name,
            employee_code: result.employee.employee_code,
            employee_type: result.employee.employee_type,
            geo_fence_enabled: result.employee.geo_fence_enabled,
            designation_name: result.employee.designation?.title || '',
            unit_name: result.employee.unit?.name || '',
            status: result.employee.status,
          });
          setGeoFenceEnabled(result.employee.geo_fence_enabled ?? true);
          setSelectedEmployeeType(result.employee.employee_type);
        }

        // ✅ Update locations
        if (result.locations && result.locations.length > 0) {
          setEmployeeLocations(result.locations);
        } else {
          setEmployeeLocations([]);
        }

        // ✅ Update summary
        if (result.summary) {
          setLocationSummary(result.summary);
        }
      } else {
        setEmployee(null);
        setEmployeeLocations([]);
        setLocationSummary(null);
        setError('No data found');
      }
    } catch (error: any) {
      console.error('Error fetching employee data:', error);
      setError(error.message || 'Failed to fetch employee data');
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch all available office locations
  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const officeRes = await apiClient.get('/locations/office/active/all');
      const officeData = officeRes.data || officeRes;

      // ✅ Map the response to match the expected format
      const mappedLocations = (officeData || []).map((loc: any) => ({
        id: loc.id,
        name: loc.office_name || loc.name,  // ✅ Handle both field names
        address: loc.address || null,
        latitude: loc.latitude || 0,
        longitude: loc.longitude || 0,
        radius: loc.radius || 0,
        status: loc.status || 1,
      }));

      setOfficeLocations(mappedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchEmployeeData();
      fetchLocations();
    }
  }, [id, fetchEmployeeData, fetchLocations]);

  // Handle location toggle in add form
  const handleLocationToggle = (locationId: number) => {
    setFormData(prev => {
      const currentIds = prev.office_location_ids;
      const isSelected = currentIds.includes(locationId);
      const newIds = isSelected
        ? currentIds.filter(id => id !== locationId)
        : [...currentIds, locationId];
      return { ...prev, office_location_ids: newIds };
    });
    setFormError('');
  };

  // Handle select all in add form
  const handleSelectAll = () => {
    const availableLocations = getAvailableLocations();
    const allIds = availableLocations.map(loc => loc.id);

    if (selectAll) {
      setFormData(prev => ({
        ...prev,
        office_location_ids: prev.office_location_ids.filter(id => !allIds.includes(id)),
      }));
    } else {
      const currentIds = formData.office_location_ids;
      const newIds = Array.from(new Set([...currentIds, ...allIds]));
      setFormData(prev => ({ ...prev, office_location_ids: newIds }));
    }
    setSelectAll(!selectAll);
  };

  // Get available locations (not already assigned)
  const getAvailableLocations = () => {
    const assignedIds = employeeLocations.map(loc => loc.office_location_id);
    return officeLocations.filter(office => !assignedIds.includes(office.id));
  };

  // Update Employee Type
  const handleUpdateEmployeeType = async () => {
    if (selectedEmployeeType === null || selectedEmployeeType === employee?.employee_type) {
      return;
    }

    setUpdatingType(true);
    try {
      await apiClient.patch(`/employees/${id}`, {
        employee_type: selectedEmployeeType,
      });

      setEmployee(prev => ({ ...prev!, employee_type: selectedEmployeeType }));
      toast.success('Employee type updated successfully!');

      await fetchEmployeeData();
    } catch (error: any) {
      console.error('Error updating employee type:', error);
      toast.error(error.message || 'Failed to update employee type');
    } finally {
      setUpdatingType(false);
    }
  };

  // Add locations
  const handleAddLocations = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.office_location_ids.length === 0) {
      setFormError('Please select at least one office location');
      return;
    }

    const assignedIds = employeeLocations.map(loc => loc.office_location_id);
    const invalidIds = formData.office_location_ids.filter(id => assignedIds.includes(id));

    if (invalidIds.length > 0) {
      setFormError(`Some selected locations are already assigned`);
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const payload: AssignLocationPayload = {
        employee_id: parseInt(id!),
        location_type: formData.location_type,
        office_location_ids: formData.office_location_ids,
      };

      await apiClient.post('/type-assign/assign-location', payload);

      const count = formData.office_location_ids.length;
      toast.success(`${count} location${count > 1 ? 's' : ''} added successfully!`);

      setFormData({
        employee_id: parseInt(id!),
        location_type: 1,
        office_location_ids: [],
      });
      setSelectAll(false);
      setShowAddForm(false);

      await fetchEmployeeData();
    } catch (error: any) {
      console.error('Error adding locations:', error);
      setFormError(error.message || 'Failed to add locations');
      toast.error('Failed to add locations');
    } finally {
      setSaving(false);
    }
  };

  // Toggle geofence
  const handleToggleGeofence = async () => {
    setUpdatingGeoFence(true);
    try {
      const newStatus = !geoFenceEnabled;
      await apiClient.patch(`/employees/${id}/toggle-geofence`, {
        enabled: newStatus,
      });

      setGeoFenceEnabled(newStatus);
      toast.success(`Geofence ${newStatus ? 'enabled' : 'disabled'} successfully!`);

      await fetchEmployeeData();
    } catch (error: any) {
      console.error('Error toggling geofence:', error);
      toast.error(error.message || 'Failed to toggle geofence');
    } finally {
      setUpdatingGeoFence(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (locationId: number, locationName: string) => {
    setLocationToDelete({ id: locationId, name: locationName });
    setDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!locationToDelete) return;

    setDeleting(true);
    try {
      await apiClient.delete(`/type-assign/employee-locations/${locationToDelete.id}`);
      toast.success(`"${locationToDelete.name}" removed successfully!`);
      setDeleteModal(false);
      setLocationToDelete(null);

      await fetchEmployeeData();
    } catch (error: any) {
      console.error('Error removing location:', error);
      toast.error(error.message || 'Failed to remove location');
    } finally {
      setDeleting(false);
    }
  };

  // Confirm remove all
  const confirmRemoveAll = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/type-assign/employees/${id}/locations`);
      toast.success('All locations removed successfully!');
      setRemoveAllModal(false);

      await fetchEmployeeData();
    } catch (error: any) {
      console.error('Error removing all locations:', error);
      toast.error(error.message || 'Failed to remove locations');
    } finally {
      setDeleting(false);
    }
  };

  const getEmployeeTypeBadge = (type: number | null) => {
    if (!type) return <Badge color="secondary">Not Set</Badge>;
    const labels: Record<number, { color: string; label: string }> = {
      1: { color: 'primary', label: 'Office' },
      2: { color: 'warning', label: 'Site Engineer' },
      3: { color: 'success', label: 'Executive' },
    };
    const info = labels[type] || { color: 'secondary', label: 'Unknown' };
    return <Badge color={info.color}>{info.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" className="me-2" />
            <span>Loading employee data...</span>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            <h5>Error Loading Employee</h5>
            <p>{error || 'Employee not found'}</p>
            <Button color="primary" onClick={() => navigate(-1)}>
              <ArrowLeft size={14} className="me-1" />
              Go Back
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  const availableLocations = getAvailableLocations();

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Location Assignment" pageTitle="Employees" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <CardTitle tag="h5" className="mb-0 d-flex align-items-center gap-2">
                  <User size={18} />
                  <span>{employee.name}</span>
                  <Badge color="secondary" className="ms-2">
                    {employee.employee_code}
                  </Badge>
                </CardTitle>
                <div className="d-flex gap-2">
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => navigate('/employees')}
                  >
                    <ArrowLeft size={14} className="me-1" />
                    Back
                  </Button>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => { fetchEmployeeData(); fetchLocations(); }}
                    disabled={loading || saving}
                  >
                    <RefreshCw size={14} className="me-1" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>

              <CardBody>
                {/* Employee Type & Geofence */}
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="border rounded-3 p-3">
                      <div className="text-muted small text-uppercase">Employee Type</div>
                      <div className="d-flex align-items-center gap-3 mt-1">
                        <div className="fw-bold">
                          {getEmployeeTypeBadge(employee.employee_type)}
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Input
                            type="select"
                            value={selectedEmployeeType || ''}
                            onChange={(e) => setSelectedEmployeeType(Number(e.target.value) || null)}
                            style={{ width: '150px' }}
                            bsSize="sm"
                          >
                            <option value="">Select Type</option>
                            {EMPLOYEE_TYPES.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </Input>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleUpdateEmployeeType}
                            disabled={updatingType || selectedEmployeeType === employee.employee_type}
                          >
                            {updatingType ? <Spinner size="sm" /> : 'Update'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded-3 p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="text-muted small text-uppercase">Geofence</div>
                          <div className="mt-1">
                            {geoFenceEnabled ? (
                              <Badge color="success" className="d-flex align-items-center gap-1" style={{ fontSize: '14px' }}>
                                <Shield size={14} />
                                Enabled
                              </Badge>
                            ) : (
                              <Badge color="secondary" className="d-flex align-items-center gap-1" style={{ fontSize: '14px' }}>
                                <ShieldOff size={14} />
                                Disabled
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          color={geoFenceEnabled ? "warning" : "success"}
                          size="sm"
                          onClick={handleToggleGeofence}
                          disabled={updatingGeoFence}
                        >
                          {updatingGeoFence ? <Spinner size="sm" /> : (geoFenceEnabled ? 'Disable' : 'Enable')}
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Locations List */}
                <div className="border-top pt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      Assigned Locations ({employeeLocations.length})
                      {locationSummary && (
                        <Badge color="info" className="ms-2">
                          {locationSummary.active_locations} Active
                        </Badge>
                      )}
                    </h6>
                    <div className="d-flex gap-2">
                      {employeeLocations.length > 0 && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => setRemoveAllModal(true)}
                          disabled={saving}
                        >
                          <Trash2 size={14} className="me-1" />
                          Remove All
                        </Button>
                      )}
                      {!showAddForm ? (
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => {
                            setShowAddForm(true);
                            setFormError('');
                            setSelectAll(false);
                          }}
                        >
                          <Plus size={14} className="me-1" />
                          Add Locations
                        </Button>
                      ) : (
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={() => {
                            setShowAddForm(false);
                            setFormError('');
                            setFormData({
                              employee_id: parseInt(id!),
                              location_type: 1,
                              office_location_ids: [],
                            });
                            setSelectAll(false);
                          }}
                        >
                          <X size={14} className="me-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Add Location Form */}
                  {showAddForm && (
                    <>
                      {formError && <Alert color="danger" className="mb-3">{formError}</Alert>}
                      <Form onSubmit={handleAddLocations} className="mb-3">
                        <FormGroup>
                          <Label className="fw-bold text-muted small text-uppercase">
                            Select Office Locations *
                          </Label>
                          <div className="border rounded p-3 bg-light">
                            {availableLocations.length === 0 ? (
                              <div className="text-muted small">
                                <i>All available offices are already assigned.</i>
                              </div>
                            ) : (
                              <>
                                <div className="mb-2 pb-2 border-bottom">
                                  <FormGroup check className="mb-0">
                                    <Label check className="fw-bold">
                                      <Input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                      />
                                      <span className="ms-2">
                                        {selectAll ? 'Deselect All' : 'Select All'}
                                      </span>
                                      <Badge color="secondary" className="ms-2">
                                        {availableLocations.length} available
                                      </Badge>
                                    </Label>
                                  </FormGroup>
                                </div>
                                <Row>
                                  {availableLocations.map((office) => {
                                    const isSelected = formData.office_location_ids.includes(office.id);
                                    return (
                                      <Col key={office.id} md={6} lg={4} className="mb-2">
                                        <FormGroup check>
                                          <Label check>
                                            <Input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => handleLocationToggle(office.id)}
                                            />
                                            <span className="ms-2">{office.name}</span>
                                          </Label>
                                        </FormGroup>
                                      </Col>
                                    );
                                  })}
                                </Row>
                                <div className="mt-2 pt-2 border-top">
                                  <small className="text-muted">
                                    Selected: <strong>{formData.office_location_ids.length}</strong> location{formData.office_location_ids.length !== 1 ? 's' : ''}
                                  </small>
                                </div>
                              </>
                            )}
                          </div>
                        </FormGroup>
                        <div className="d-flex gap-2 mt-3">
                          <Button
                            type="submit"
                            color="primary"
                            disabled={saving || availableLocations.length === 0 || formData.office_location_ids.length === 0}
                          >
                            {saving ? <Spinner size="sm" className="me-2" /> : <Plus size={14} className="me-1" />}
                            {saving ? 'Adding...' : `Add ${formData.office_location_ids.length || ''} Location${formData.office_location_ids.length !== 1 ? 's' : ''}`}
                          </Button>
                          <Button
                            color="secondary"
                            onClick={() => {
                              setFormData({
                                employee_id: parseInt(id!),
                                location_type: 1,
                                office_location_ids: [],
                              });
                              setSelectAll(false);
                            }}
                            disabled={saving}
                          >
                            Clear
                          </Button>
                        </div>
                      </Form>
                    </>
                  )}

                  {/* ✅ Locations Table - Fixed to use 'name' instead of 'office_name' */}
                  {employeeLocations.length > 0 ? (
                    <div className="table-responsive">
                      <Table striped className="mb-0">
                        <thead>
                          <tr>
                            <th>SI.No</th>
                            <th>Office Name</th>
                            <th>Radius</th>
                            <th>Status</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeLocations.map((location, index) => (
                            <tr key={location.id}>
                              <td>{index + 1}</td>
                              <td>
                                <span className="fw-semibold">
                                  {location.office_location?.name || `Location ${location.office_location_id}`}
                                </span>
                              </td>
                              <td>{location.office_location?.radius || 0}m</td>
                              <td>
                                <Badge color={location.office_location?.status === 1 ? 'success' : 'secondary'}>
                                  {location.office_location?.status === 1 ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => openDeleteModal(
                                    location.id,
                                    location.office_location?.name || `Location ${location.office_location_id}`
                                  )}
                                  disabled={saving}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert color="info" className="mb-0">
                      <Info size={16} className="me-2" />
                      <span>No locations assigned. Click <strong>"Add Locations"</strong> to assign office locations.</span>
                    </Alert>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          <Trash2 size={18} className="text-danger me-2" />
          Remove Location
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to remove <strong>"{locationToDelete?.name}"</strong> from this employee?
          </p>
          <Alert color="warning" className="mb-0">
            <Info size={16} className="me-2" />
            This action cannot be undone.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" className="me-2" /> : null}
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove All Confirmation Modal */}
      <Modal isOpen={removeAllModal} toggle={() => setRemoveAllModal(false)}>
        <ModalHeader toggle={() => setRemoveAllModal(false)}>
          <Trash2 size={18} className="text-danger me-2" />
          Remove All Locations
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to remove <strong>ALL {employeeLocations.length} locations</strong> from this employee?
          </p>
          <Alert color="danger" className="mb-0">
            <Info size={16} className="me-2" />
            This action cannot be undone and will remove all assigned office locations.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRemoveAllModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmRemoveAll} disabled={deleting}>
            {deleting ? <Spinner size="sm" className="me-2" /> : null}
            {deleting ? 'Removing...' : 'Remove All'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default EmployeeTypeAssignment;